import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Scale, AlertTriangle, CheckCircle, XCircle, Shield, Gavel, Mail } from 'lucide-react';
import Footer from '../common/Footer';

const TermsOfService = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen relative flex flex-col overflow-hidden animate-fadeIn">
      {/* Background Image - Same as Login */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('/images/Map-2.png')`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/40 via-gray-900/30 to-blue-800/40"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 p-4 sm:p-6">
        <div className="max-w-4xl mx-auto">
          {/* Main Card - Same as Login */}
          <div className="bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 rounded-3xl shadow-2xl p-4 sm:p-6 md:p-8 animate-scaleIn">
            
            {/* Logo & Header */}
            <div className="text-center mb-6 animate-slideDown">
              <Link to="/">
                <img 
                  src="/images/logo.png" 
                  alt="QuickLook Logo" 
                  className="h-24 sm:h-28 md:h-32 w-auto mx-auto drop-shadow-2xl mb-4"
                />
              </Link>
              <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-2xl mb-4">
                <Scale className="w-6 h-6 text-blue-400" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Terms of Service</h1>
              <p className="text-gray-400 text-sm">Effective Date: April 16, 2026</p>
            </div>

            {/* Back & Privacy Links */}
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() => navigate('/')}
                className="flex items-center text-gray-300 hover:text-white text-sm"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Login
              </button>
              <Link to="/privacy" className="text-sm text-blue-400 hover:text-blue-300">
                Privacy Policy →
              </Link>
            </div>

            {/* Agreement Notice */}
            <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-amber-200 text-sm">
                  By accessing or using QuickLook, you agree to be bound by these Terms of Service. 
                  If you disagree with any part of these terms, you may not access the Service.
                </p>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="max-h-[50vh] overflow-y-auto pr-2 space-y-4 scrollbar-hide">
              
              {/* Section 1: Acceptance */}
              <div className="bg-slate-900/50 rounded-2xl p-4 sm:p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-500/20 rounded-xl">
                    <CheckCircle className="w-5 h-5 text-blue-400" />
                  </div>
                  <h2 className="text-base sm:text-lg font-bold text-white">1. Acceptance of Terms</h2>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  QuickLook provides a shipment tracking and logistics management platform subject to these 
                  Terms. By creating an account, you acknowledge that you have read, understood, and agree 
                  to be bound by these Terms.
                </p>
              </div>

              {/* Section 2: Description */}
              <div className="bg-slate-900/50 rounded-2xl p-4 sm:p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-500/20 rounded-xl">
                    <Shield className="w-5 h-5 text-blue-400" />
                  </div>
                  <h2 className="text-base sm:text-lg font-bold text-white">2. Description of Service</h2>
                </div>
                <ul className="space-y-1.5 text-gray-400 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span><strong className="text-gray-300">Shipment Tracking:</strong> Real-time tracking of Air-Export and Sea-Export shipments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span><strong className="text-gray-300">Notifications:</strong> Push notifications and alerts for shipment status updates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span><strong className="text-gray-300">Invoice Management:</strong> Generation and management of shipping invoices</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span><strong className="text-gray-300">Booking Management:</strong> Create and manage new shipment bookings</span>
                  </li>
                </ul>
              </div>

              {/* Section 3: User Accounts */}
              <div className="bg-slate-900/50 rounded-2xl p-4 sm:p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-500/20 rounded-xl">
                    <Shield className="w-5 h-5 text-blue-400" />
                  </div>
                  <h2 className="text-base sm:text-lg font-bold text-white">3. User Accounts</h2>
                </div>
                <ul className="space-y-1.5 text-gray-400 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span>You must provide accurate, complete, and current information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span>You are responsible for maintaining confidentiality of your credentials</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span>You must be at least 18 years old to use this Service</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                    <span>Notify us immediately of any unauthorized use of your account</span>
                  </li>
                </ul>
              </div>

              {/* Section 4: Acceptable Use */}
              <div className="bg-slate-900/50 rounded-2xl p-4 sm:p-5">
                <h2 className="text-base sm:text-lg font-bold text-white mb-4">4. Acceptable Use Policy</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-semibold text-green-400 text-sm mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      You May:
                    </h3>
                    <ul className="space-y-1.5 text-gray-400 text-sm">
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-1.5 flex-shrink-0"></span>
                        <span>Use for legitimate business purposes</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-1.5 flex-shrink-0"></span>
                        <span>Track and manage your shipments</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-1.5 flex-shrink-0"></span>
                        <span>Access and export your data</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-semibold text-red-400 text-sm mb-2 flex items-center gap-2">
                      <XCircle className="w-4 h-4" />
                      You May Not:
                    </h3>
                    <ul className="space-y-1.5 text-gray-400 text-sm">
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-1.5 flex-shrink-0"></span>
                        <span>Use for any illegal purpose</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-1.5 flex-shrink-0"></span>
                        <span>Attempt unauthorized access</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-1.5 flex-shrink-0"></span>
                        <span>Transmit malware or viruses</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 5: IP */}
              <div className="bg-slate-900/50 rounded-2xl p-4 sm:p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-500/20 rounded-xl">
                    <Gavel className="w-5 h-5 text-blue-400" />
                  </div>
                  <h2 className="text-base sm:text-lg font-bold text-white">5. Intellectual Property</h2>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  The Service and its content are owned by QuickLook and protected by international copyright 
                  and trademark laws. "QuickLook" and associated logos are our trademarks.
                </p>
              </div>

              {/* Section 6: Limitation */}
              <div className="bg-slate-900/50 rounded-2xl p-4 sm:p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-amber-500/20 rounded-xl">
                    <AlertTriangle className="w-5 h-5 text-amber-400" />
                  </div>
                  <h2 className="text-base sm:text-lg font-bold text-white">6. Limitation of Liability</h2>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  QuickLook shall not be liable for any indirect, incidental, special, consequential, or 
                  punitive damages. The Service is provided "AS IS" without warranties of any kind.
                </p>
              </div>

              {/* Section 7: Termination */}
              <div className="bg-slate-900/50 rounded-2xl p-4 sm:p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-red-500/20 rounded-xl">
                    <XCircle className="w-5 h-5 text-red-400" />
                  </div>
                  <h2 className="text-base sm:text-lg font-bold text-white">7. Termination</h2>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  We may terminate or suspend your account immediately for any breach of these Terms. 
                  Upon termination, your right to use the Service will cease immediately.
                </p>
              </div>

              {/* Section 8: Governing Law */}
              <div className="bg-slate-900/50 rounded-2xl p-4 sm:p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-500/20 rounded-xl">
                    <Scale className="w-5 h-5 text-blue-400" />
                  </div>
                  <h2 className="text-base sm:text-lg font-bold text-white">8. Governing Law</h2>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  These Terms shall be governed by the laws of India. Any disputes shall be subject to 
                  the exclusive jurisdiction of the courts in New Delhi, India.
                </p>
              </div>

              {/* Section 9: Changes */}
              <div className="bg-slate-900/50 rounded-2xl p-4 sm:p-5">
                <h2 className="text-base sm:text-lg font-bold text-white mb-3">9. Changes to Terms</h2>
                <p className="text-gray-400 text-sm leading-relaxed">
                  We reserve the right to modify these Terms at any time. If a revision is material, 
                  we will provide at least 30 days' notice prior to any new terms taking effect.
                </p>
              </div>

              {/* Contact */}
              <div className="bg-blue-500/20 rounded-2xl p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-500/30 rounded-xl">
                    <Mail className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-base sm:text-lg font-bold text-white mb-2">10. Contact Us</h2>
                    <div className="space-y-1 text-gray-300 text-sm">
                      <p><strong>Developer:</strong> HMR Spedition</p>
                      <p><strong>Email:</strong> info@hmrspedition.com</p>
                      <p><strong>Website:</strong> https://quicklook.in</p>
                      <p><strong>Address:</strong> New Delhi, India</p>
                    </div>
                    <p className="text-gray-400 text-xs mt-3">
                      For any questions about these Terms, please contact us at the email address above.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Links */}
            <div className="mt-6 pt-4 border-t border-slate-600/50">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-gray-500 text-xs">
                  © {new Date().getFullYear()} QuickLook. All rights reserved.
                </p>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-blue-400 font-semibold">
                    Terms of Service
                  </span>
                  <span className="text-gray-600">|</span>
                  <Link to="/privacy" className="text-xs text-gray-400 hover:text-white">
                    Privacy Policy
                  </Link>
                </div>
              </div>
            </div>

            <Footer tone="dark" className="mt-4" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
